import React, {
    FC,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useState,
} from "react";
import { ISideBarProject } from "../../../common/api_interfaces/common/ISideBar";
import { AppContext } from "../../../App";
import Tooltip from "../../UiLib/CustomTooltip/CustomTooltip";
import { Link } from "react-router-dom";
import ArrowButton from "../../buttons/ArrowButton/ArrowButton";
import { Context } from "../../..";
import BoardComponent from "./BoardComponent";
import SubprojectComponent from "./SubprojectComponent";
import { personalSettings } from "../../../common/enums/personalSettings";

interface IProjectComponent {
    project: ISideBarProject;
}

const ProjectComponent: FC<IProjectComponent> = ({ project }) => {
    const { navBarInfo, toggleProject } = useContext(AppContext);
    const { store } = useContext(Context);
    
    const projectOpen = useMemo(() => 
        navBarInfo?.openProjectIds?.includes(project.id),
        [navBarInfo?.openProjectIds, project.id]
    );

    const projectHighlighted = useMemo(() =>
        navBarInfo?.projectId === project.id &&
        navBarInfo?.boardId === undefined &&
        navBarInfo?.subprojectId === undefined,
        [navBarInfo?.projectId, navBarInfo?.boardId, navBarInfo?.subprojectId, project.id]
    );

    const projectMarked = useMemo(() =>
        !projectHighlighted && navBarInfo?.projectId === project.id,
        [projectHighlighted, navBarInfo?.projectId, project.id]
    );

    const [showProxyBoards, setShowProxyBoards] = useState<boolean>(false);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);

    const handleToggleProject = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        toggleProject(project.id);
    }, [toggleProject, project.id]);

    const handleLinkClick = useCallback(() => {
        if (projectHighlighted) {
            toggleProject(project.id);
        }
    }, [projectHighlighted, toggleProject, project.id]);

    useEffect(() => {
        const showProxy = store.personalSettings.find(
            s => s.paramName === personalSettings.showProxyBoardsLeftMenu
        );
        if (showProxy) setShowProxyBoards(showProxy.value as boolean);
        else setShowProxyBoards(true);
    }, [store.personalSettings]);

    useLayoutEffect(() => {
        const container = document.getElementById(`nav-panel-project-${project.id}`);
        const name = document.getElementById(`nav-panel-project-name-${project.id}`);

        if (!container || !name) return;

        if (name.offsetWidth > container.clientWidth) setShowTooltip(true);
    }, [project.id]);

    const filteredBoards = useMemo(() => 
        project.boards.filter(b => showProxyBoards || !b.isProxyBoard),
        [project.boards, showProxyBoards]
    );

    const boardsContent = useMemo(() => {
        if (!filteredBoards.length) return null;
        return (
            <>
                <div className="nav-panel_divider">ДОСКИ</div>
                {filteredBoards.map((board) => (
                    <BoardComponent
                        key={board.id}
                        board={board}
                        showProxyBoards={showProxyBoards}
                    />
                ))}
            </>
        );
    }, [filteredBoards, showProxyBoards]);

    const subprojectsContent = useMemo(() => {
        if (!project.subprojects?.length) return null;
        return (
            <>
                <div className="nav-panel_divider">ПОДПРОЕКТЫ</div>
                {project.subprojects.map((subproject) => (
                    <SubprojectComponent
                        key={subproject.id}
                        subproject={subproject}
                        showProxyBoards={showProxyBoards}
                    />
                ))}
            </>
        );
    }, [project.subprojects, showProxyBoards]);

    return (
        <div className={`nav-panel_project-container ${projectOpen ? "open" : ""}`}>
            <Tooltip
                text={project.name ?? ""}
                offset={5}
                placement={"right"}
                style={{
                    width: "fit-content",
                    maxWidth: "250px",
                    wordBreak: "break-all",
                    display: showTooltip ? "block" : "none",
                }}
            >
                <Link
                    to={"/project/" + project.id}
                    className={`nav-panel_project ${projectOpen ? "open" : ""} ${
                        projectHighlighted ? "highlighted" : ""
                    } ${projectMarked ? "with-mark" : ""}`}
                    onClick={handleLinkClick}
                >
                    <div id={`nav-panel-project-${project.id}`} className="nav-panel_label">
                        <span id={`nav-panel-project-name-${project.id}`}>
                            {project.name}
                        </span>
                    </div>
                    <ArrowButton
                        onClick={handleToggleProject}
                        direction={projectOpen ? "top" : "bottom"}
                        whiteArrow={projectHighlighted}
                    />
                </Link>
            </Tooltip>
            {projectOpen ? (
                <div className="nav-panel_project-children">
                    <div className="nav-panel_project-boards">
                        {boardsContent}
                    </div>
                    {subprojectsContent}
                </div>
            ) : null}
        </div>
    );
};

export default ProjectComponent;
